import { Injectable } from '@angular/core';

import { Fixture } from '@cds/betting-offer';
import { FallbackMapping, PicksView } from '@cds/betting-offer/domain-specific';
import { toDictionary } from '@frontend/sports/common/core/utils/collection';
import { hasValue } from '@frontend/sports/common/core/utils/extended-types';
import { keys } from 'lodash-es';

import { MyBetsBet } from '../../../my-bets-base/models/my-bets-viewmodels';

@Injectable()
export class ReBetFiltersService {
    getRelatedBets(bets: MyBetsBet[], picksView: PicksView): PicksView | undefined {
        const fixtures = this.filterFixtures(bets, picksView.fixturePage.fixtures, picksView.liveFallbacks);
        const liveFallbacks = this.filterLiveFallbacks(bets, picksView.liveFallbacks);

        return fixtures.length || keys(liveFallbacks).length
            ? {
                  betBuilderSourceFixtures: {},
                  fixturePage: {
                      fixtures,
                      totalCount: fixtures.length,
                  },
                  liveFallbacks,
                  contextualInfo: {
                      optionsContextualInfo: {},
                      resultsContextualInfo: {},
                  },
                  betBuilderPricingGroups: {},
              }
            : undefined;
    }

    private filterLiveFallbacks(bets: MyBetsBet[], fallbacks: { [key: number]: FallbackMapping }): { [key: number]: FallbackMapping } {
        const liveFallbacks = bets.filter((b) => b.resultInfo && fallbacks[b.resultInfo.optionId]);

        return toDictionary(
            liveFallbacks,
            (p) => p.resultInfo!.optionId,
            (p) => fallbacks[p.resultInfo!.optionId],
        );
    }

    private filterFixtures(bets: MyBetsBet[], fixtures: Fixture[], fallback: { [key: number]: FallbackMapping }): Fixture[] {
        return bets.map((b) => this.getFixture(b, fixtures, fallback)).filter(hasValue);
    }

    private getFixture = (bet: MyBetsBet, fixtures: Fixture[], fallback: { [key: number]: FallbackMapping }): Fixture | undefined => {
        if (bet.resultInfo) {
            return this.getV1Sport(bet, fixtures, fallback);
        }
        if (bet.optionMarketInfo) {
            return this.getOptionMarket(bet, fixtures);
        }
        if (bet.participantInfo) {
            return this.getParticipantMarket(bet, fixtures);
        }

        return;
    };

    private getV1Sport = (bet: MyBetsBet, fixtures: Fixture[], fallback: { [key: number]: FallbackMapping }): Fixture | undefined => {
        const currentOptionId = bet.resultInfo!.optionId;

        const fixtureId = fallback[currentOptionId]?.fixtureId ?? bet.resultInfo!.event?.id ?? bet.resultInfo!.eventId.toString();
        const marketId = fallback[currentOptionId]?.gameId ?? bet.resultInfo!.marketId;
        const optionId = fallback[currentOptionId]?.resultId ?? bet.resultInfo!.optionId;

        return fixtures.find((f) =>
            f.id === fixtureId && f.hybridFixtureData != null
                ? f.optionMarkets.some((g) => g.id === marketId && g.options.some((o) => o.id === optionId))
                : f.games.some((g) => g.id === marketId && g.results.some((o) => o.id === optionId)),
        );
    };

    private getOptionMarket = (bet: MyBetsBet, fixtures: Fixture[]): Fixture | undefined => {
        return fixtures.find(
            (f) => f.id === bet.optionMarketInfo!.fixtureCompoundId && f.optionMarkets.some((om) => om.id === bet.optionMarketInfo!.optionMarketId),
        );
    };

    private getParticipantMarket = (bet: MyBetsBet, fixtures: Fixture[]): Fixture | undefined => {
        return fixtures.find(
            (f) =>
                f.id === bet.participantInfo!.compoundId &&
                keys(bet.participantInfo!.participantInfo).every((info) => f.participants.some((p) => p.id && p.id.toString() === info)),
        );
    };
}
